import AddCommentIcon from "@mui/icons-material/AddComment";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import CloseIcon from "@mui/icons-material/Close";
import MessageIcon from "@mui/icons-material/Message";
import PageviewIcon from "@mui/icons-material/Pageview";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import EmptyInboxPlaceholder from "_shared/_views/assets/empty-inbox-all-caught-up.svg";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_TableInstance as TableInstance,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import {
  ToolbarButtons,
  searchFieldSpacing,
} from "_shared/_views/tables/table-toolbar";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import {
  getErrorLoadingForumsByQuery,
  getForumQueryByInterface,
  getForumsByQuery,
  getIsLoadingForumsByQuery,
  populateForumsByQuery,
  updateForumInterfaceQuery,
} from "messaging/forums/_stores/forums-redux-slice";
import Forum from "messaging/forums/forum";
import { getForumChatSubtitle, getForumChatTitle } from "messaging/forums/forum-chat-display";
import { getProposalStatusLabel } from "work/proposals/proposal-status-display";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { useGetProposalsByIdsQuery } from "work/proposals/_api/proposal-api";
import { useGetWorkAgreementsByIdsQuery } from "work/work-agreements/_api/work-agreement-api";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarActionButtons = styled("div")(() => ({
  textAlign: "right",
  whiteSpace: "nowrap",
}));
const CategoryItem = styled(ListItem)(({ theme }) => ({
  margin: 0,
  padding: 0,
  minWidth: 0,
  "& .MuiListItemText-root": {
    minWidth: 0,
  },
  "& .MuiListItemText-primary": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiListItemText-secondary": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  [theme.breakpoints.down("md")]: {
    "& .MuiListItemIcon-root": {
      display: "none",
    },
  },
}));
const EllipsisTime = styled("time")(() => ({
  display: "block",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));
const AllCaughtUp = styled("img")(({ theme }) => ({
  maxHeight: "100px",
  padding: theme.spacing(2),
}));

type ConversationTableProps = {
  renderTopToolbarCustomActions: (props: { table: TableInstance<Forum> }) => React.ReactNode;
};

const columnHelper = createMRTColumnHelper<Forum>();

export default function ConversationTable(props: Readonly<ConversationTableProps>) {
  const { renderTopToolbarCustomActions } = props;
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const navigate = useNavigate();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();
  const isMedDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const isXlDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("xl"));

  const interfaceName = "forumsIndex";

  const dispatch = useAttorneyHubDispatch();

  const forumQuery = useSelector(getForumQueryByInterface(interfaceName));
  const forums = useSelector(getForumsByQuery(forumQuery));
  const queryIsLoading = useSelector(getIsLoadingForumsByQuery(forumQuery));
  const errorLoadingQuery = useSelector(getErrorLoadingForumsByQuery(forumQuery));

  const proposalIds = useMemo(
    () =>
      (forums ?? [])
        .filter((forum) => forum.topic?.entityClass?.value === "Work.Proposal")
        .map((forum) => forum.topic?.entityId?.value)
        .filter((id): id is string => Boolean(id)),
    [forums],
  );
  const workAgreementIds = useMemo(
    () =>
      (forums ?? [])
        .filter((forum) => forum.topic?.entityClass?.value === "Work.Agreement")
        .map((forum) => forum.topic?.entityId?.value)
        .filter((id): id is string => Boolean(id)),
    [forums],
  );
  const proposalsQuery = useGetProposalsByIdsQuery(
    { proposalIds: [...new Set(proposalIds)] },
    { skip: proposalIds.length === 0 },
  );
  const workAgreementsQuery = useGetWorkAgreementsByIdsQuery(
    { workAgreementIds: [...new Set(workAgreementIds)] },
    { skip: workAgreementIds.length === 0 },
  );
  const forumEntityNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const proposal of proposalsQuery.data ?? []) {
      if (proposal.id && proposal.name) names.set(`Work.Proposal:${proposal.id}`, proposal.name);
    }
    for (const workAgreement of workAgreementsQuery.data ?? []) {
      if (workAgreement.id && workAgreement.name) names.set(`Work.Agreement:${workAgreement.id}`, workAgreement.name);
    }
    return names;
  }, [proposalsQuery.data, workAgreementsQuery.data]);

  // Only proposals carry a stage worth showing here; a work agreement has been hired and has
  // nowhere left to be (#5129). A proposal the reader may not open comes back as an error with no
  // status, and so contributes nothing.
  const forumEntityStatuses = useMemo(() => {
    const statuses = new Map<string, string>();
    for (const proposal of proposalsQuery.data ?? []) {
      const statusLabel = getProposalStatusLabel(proposal.status);
      if (proposal.id && statusLabel) statuses.set(`Work.Proposal:${proposal.id}`, statusLabel);
    }
    return statuses;
  }, [proposalsQuery.data]);

  const showAlertBanner = Boolean(errorLoadingQuery);
  const showProgressBars = queryIsLoading;
  const showSkeletons = queryIsLoading && forums === undefined && !errorLoadingQuery;

  const defaultQuery = React.useMemo(
    () =>
      new ForumQuery({
        filterParameters: new ForumFilterParameters({}),
        orderParameters: new ForumOrderParameters({
          orderBy: ForumOrderField.CreatedDate,
          orderDir: OrderDirection.Descending,
        }),
        paginationParameters: PaginationParameters.create({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        }),
      }),
    [pagination.pageIndex, pagination.pageSize]
  );


  useEffect(() => {
    if (!forumQuery) {
      dispatch(updateForumInterfaceQuery({ interfaceName, query: defaultQuery }));
      return;
    }

    // A failure has to stop this, or it does not stop at all: a rejected fetch leaves the forums
    // undefined and the loading flag false, which is exactly the condition to fetch again, so the
    // table sat on skeleton rows re-asking a server that had already said no. The communications
    // page checks its own error for the same reason, in `shouldPopulateNotificationQuery`. The
    // retry in the empty state is then the way back, and it clears the error as it goes.
    if (forumQuery && forums === undefined && !queryIsLoading && !errorLoadingQuery) {
      dispatch(
        populateForumsByQuery({
          session: session,
          query: forumQuery,
        }),
      );
    } 
  }, [forums, forumQuery, globalFilter, queryIsLoading, errorLoadingQuery, dispatch, defaultQuery, session]);

  useEffect(() => {
    const forumNameFilter = ForumName.fromJSON(columnFilters.find((f) => f.id === "name")?.value);
    const filterParameters = new ForumFilterParameters({
      name: forumNameFilter ?? undefined,
      query: globalFilter || undefined,
    });
    let orderBy = ForumOrderField.CreatedDate;
    let orderDir = OrderDirection.Descending;
    if (sorting.length > 0) {
      const sort = sorting[0];
      orderBy = getForumOrderField(sort.id);
      orderDir = sort.desc ? OrderDirection.Descending : OrderDirection.Ascending;
    }
    const orderParameters = new ForumOrderParameters({ orderBy, orderDir });
    const newQuery = new ForumQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.create({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
    });
    if (newQuery.isEqualTo(forumQuery)) return;
    dispatch(
      updateForumInterfaceQuery({
        interfaceName: "forumsIndex",
        query: newQuery,
      }),
    );
  }, [columnFilters, dispatch, forumQuery, globalFilter, pagination, sorting]);

  async function navigateToTarget(forum: Forum) {
    if (forum?.id) {
      navigate(`/communications/forums/${forum.id}`);
    }
  }

  const renderConversationNameColumn = useCallback((forum: Forum): React.ReactNode => {
    const entityClass = forum.topic?.entityClass?.value;
    const entityId = forum.topic?.entityId?.value;
    const entityKey = entityClass && entityId ? `${entityClass}:${entityId}` : undefined;
    const entityName = entityKey ? forumEntityNames.get(entityKey) : undefined;
    // The proposals come back in their own request, seconds after the forums on a cold start, so
    // say the row is waiting rather than leave the stage silently missing (#5129).
    const knownStatus = entityKey ? forumEntityStatuses.get(entityKey) : undefined;
    const isAwaitingProposal = entityClass === "Work.Proposal" && proposalsQuery.isLoading;
    const entityStatus = knownStatus ?? (isAwaitingProposal ? "Loading..." : undefined);

    return (
      <CategoryItem dense>
        <ListItemIcon>
          <MessageIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <RowLink to={forum.id ? `/communications/forums/${forum.id}` : undefined}>
              {getForumChatTitle(forum, session.user?.fullName)}
            </RowLink>
          }
          secondary={getForumChatSubtitle(forum, entityName, session.user?.fullName, entityStatus)}
          slotProps={{
            primary: { 
              sx: {
                fontWeight: "500"
              }
            },
            secondary: {
              sx: {
                fontSize: "0.75rem",
              },
            },
          }}
        />
      </CategoryItem>
    );
  }, [forumEntityNames, forumEntityStatuses, proposalsQuery.isLoading, session.user?.fullName]);

  const renderCreatedDateColumn = useCallback((forum: Forum): React.ReactNode => {
    if (!forum.createdDate) return null;
    return <EllipsisTime>{forum.createdDate.format("MM/DD/YY hh:mm A")}</EllipsisTime>;
  }, []);

  const columns = useMemo(() => [
    columnHelper.accessor(getForumNameFilterValue, {
      header: "Conversation Name",
      id: "name",
      enableSorting: true,
      enableColumnFilter: true,
      Cell: ({ row }) => renderConversationNameColumn(row.original),
    }),
    columnHelper.accessor(getForumCreatedDateSortValue, {
      header: "Created",
      id: "createdDate",
      enableSorting: true,
      enableColumnFilter: false,
      Cell: ({ row }) => renderCreatedDateColumn(row.original),
    })
  ], [renderConversationNameColumn, renderCreatedDateColumn]);

  function renderForumRowActions(forum: Forum): React.ReactNode {
    const forumTitle = getForumChatTitle(forum, session.user?.fullName);
    return (
      <RowActions
        ariaLabel={`Actions for ${forumTitle}`}
        actions={[
          {
            key: "view",
            label: "View Conversation",
            ariaLabel: `View conversation ${forumTitle}`,
            icon: <PageviewIcon fontSize="medium" />,
            onClick: () => navigate(`/communications/forums/${forum.id}`),
          },
        ]}
      />
    );
  }

  const tableOptions: MRT_TableOptions<Forum> = {
    columns,
    data: forums ?? [],
    rowCount: forums?.length,
    enableRowSelection: false,
    enableRowActions: true,
    enableTableHead: true,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        grow: 0,
        maxSize: 48,
        minSize: 48,
        size: 48,
        muiTableHeadCellProps: {
          sx: { px: 0.5 },
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            px: 0.5,
          },
        },
      },
    },
    muiSearchTextFieldProps: {
      placeholder: "Search conversations",
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: globalFilter ? (
            <InputAdornment position="end">
              <IconButton aria-label="Clear search" size="small" onClick={() => setGlobalFilter("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
    renderRowActions: ({ row }) => renderForumRowActions(row.original),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        void navigateToTarget(row.original);
      },
      sx: {
        cursor: "pointer",
        backgroundColor: "inherit",
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMedDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <ToolbarActionButtons>
              <Button
                color="primary"
                startIcon={<AddCommentIcon />}
                onClick={() => navigate("/communications/forums/compose")}
              >
                New Message
              </Button>
            </ToolbarActionButtons>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    // The table had inherited the communications page's copy along with its illustration, and told
    // anyone who searched it that "No matching communications found" — on a page of conversations.
    renderEmptyRowsFallback: emptyRowsFallback({
      icon: <AllCaughtUp alt="" src={EmptyInboxPlaceholder} />,
      isError: Boolean(errorLoadingQuery),
      isLoading: queryIsLoading,
      onRetry: () => {
        if (forumQuery) dispatch(populateForumsByQuery({ session, query: forumQuery }));
      },
      subject: "conversations",
    }),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnOrder: ["name", "createdDate", "actions"],
      showGlobalFilter: true,
    },
    manualFiltering: true,
    enableColumnFilters: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualSorting: true,
    layoutMode: "grid",
    muiTableContainerProps: { sx: { flex: 1, minWidth: 0 } },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      },
    },
    muiTopToolbarProps: {
      sx: {
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        "& > .MuiBox-root": {
          gap: 0,
          minWidth: 0,
          overflow: "hidden",
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        height: "100%",
        "& tr:only-of-type td[colspan]": {
          height: "100%",
          verticalAlign: "middle",
          padding: 0,
          borderBottom: "none",
        },
      },
    },
    muiToolbarAlertBannerProps: showAlertBanner
      ? {
          color: "error",
          title: errorLoadingQuery || "",
        }
      : {
          action: <AlertActions />,
        },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    positionGlobalFilter: "none",
    state: {
      columnFilters,
      globalFilter,
      isLoading: queryIsLoading,
      pagination,
      density: isXlDisplaySize ? "compact" : "comfortable",
      showAlertBanner,
      showProgressBars,
      sorting,
      rowSelection,
      showSkeletons,
    },
  }

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToCommunications) return <NotAuthorized />;

  return <MRTNoMemo<Forum> options={tableOptions} />;
}

function getForumNameFilterValue(forum: Forum): string {
  return forum.name?.toString() ?? "";
}

function getForumCreatedDateSortValue(forum: Forum): number {
  return forum.createdDate?.value ?? 0;
}

function getForumOrderField(columnId: string): ForumOrderField {
  if (columnId === "name") return ForumOrderField.Name;
  return ForumOrderField.CreatedDate;
}
